import type { TaskSummary } from "../../lib/api";
export const TASKS_CHANGED = "agntspace:tasks-changed";
export const taskKey = (task: { slug: string; project: string }) =>
	JSON.stringify([task.project, task.slug]);
export const emitTasksChanged = () =>
	window.dispatchEvent(new CustomEvent(TASKS_CHANGED));
export const TASK_STATUSES = [
	"delegated",
	"in_progress",
	"review",
	"blocked",
	"done",
	"cancelled",
];
export const TASK_PRIORITIES = ["low", "normal", "high", "urgent"];
export const TASK_MODES: Record<string, string> = {
	review: "Review first",
	jdi: "Just do it",
	recommend: "Recommend",
	watch: "Watch",
};
export const statusLabel = (status: string) =>
	({
		delegated: "Delegated",
		in_progress: "In progress",
		review: "Review",
		blocked: "Blocked",
		done: "Done",
		cancelled: "Cancelled",
	})[status] ?? status;
export const taskColumns = TASK_STATUSES.map((id) => ({
	id,
	label: statusLabel(id),
}));
export function taskSummary(
	detail: { slug: string; metadata: Record<string, unknown> },
	project: string,
): TaskSummary {
	const m = detail.metadata;
	return {
		slug: detail.slug,
		project: String(m.project || project),
		title: String(m.title || detail.slug),
		status: String(m.status || "delegated"),
		mode: String(m.mode || "review"),
		domain: String(m.domain || "general"),
		priority: String(m.priority || "normal"),
		delegated: String(m.delegated || ""),
		due: String(m.due || ""),
		assigned_agent: String(m.assigned_agent || ""),
	};
}
export function taskBrief(detail: { brief?: string; content: string }): string {
	return (
		detail.brief ??
		detail.content
			.match(/^## Brief[ \t]*\n([\s\S]*?)(?=^## |(?![\s\S]))/m)?.[1]
			?.trim() ??
		""
	);
}
function formatFocusTotal(seconds: number): string {
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	return hours > 0
		? `${hours}h ${String(minutes % 60).padStart(2, "0")}m`
		: `${minutes}m`;
}

/**
 * Render focus effort on a task: completed work, plus discarded attempts as a
 * separate trailing signal ("sat down 8 times, finished 5").
 *
 * The middle branch is the one that matters: with only discarded attempts,
 * "None yet" would be a lie — the user DID sit down, they just bailed. Zero
 * discards render nothing rather than a "0 discarded" that reads as failure.
 */
export function formatFocusEffort(
	totals: { sessions: number; seconds: number; abandoned: number } | null,
	t: (key: string, fallback: string, opts?: Record<string, unknown>) => string,
): string {
	if (!totals) return t("tasks.noFocusYet", "None yet");
	const discarded =
		totals.abandoned > 0
			? ` · ${t("tasks.focusDiscarded", "{{n}} discarded", { n: totals.abandoned })}`
			: "";
	if (totals.sessions > 0) {
		const done = `${formatFocusTotal(totals.seconds)} · ${totals.sessions} ${t("tasks.focusSessions", "session(s)")}`;
		return `${done}${discarded}`;
	}
	if (totals.abandoned > 0) {
		return `${t("tasks.noFocusCompleted", "None completed")}${discarded}`;
	}
	return t("tasks.noFocusYet", "None yet");
}
